import { Field, Form, Formik } from "formik";
import { ScrollToFirstError } from "utils";
import { Button, SelectComponent, Textarea, Checkbox } from "shared";
import { Input, DropFileInput } from "shared";
import { InvestmentItem } from "../../../types";
import { PreciousStonesValues } from "./types";
import { preciousStonesSchema } from "./validationSchemas";
import { RELIANCE_CONSENT_LABEL } from "./constants";

interface PreciousStonesFormProps {
  item: InvestmentItem;
  onSubmit: (values: PreciousStonesValues) => void;
  onCancel: () => void;
  isPending: boolean;
  currencyOptions: any[];
  onRemovedContract: (file: { name: string; url: string }) => void;
}

const PreciousStonesForm = ({
  item,
  onSubmit,
  onCancel,
  isPending,
  currencyOptions,
  onRemovedContract,
}: PreciousStonesFormProps) => {
  const existingContractFiles = (item.files ?? []).filter(
    (f: any) => f.role === "contract",
  );

  const initialValues: PreciousStonesValues = {
    name: item.name ?? "",
    primaryValue:
      item.primaryValue !== null && item.primaryValue !== undefined
        ? String(item.primaryValue)
        : "",
    currencyUuid: item.currencyUuid ?? "",
    weight: item.details?.weight != null ? String(item.details.weight) : "",
    cut: String(item.details?.cut ?? ""),
    color: String(item.details?.color ?? ""),
    clarity: String(item.details?.clarity ?? ""),
    additionalInfo: item.additionalInfo ?? "",
    contractFile: null,
    relianceCanView: Boolean(item.relianceCanView ?? false),
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={preciousStonesSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formik) => (
        <Form>
          <ScrollToFirstError />
          <div className="modal-form__fields">
            <Field
              as={Input}
              name="name"
              label="Nazwa"
              type="text"
              error={
                (formik.touched.name || formik.submitCount > 0) &&
                formik.errors.name
                  ? formik.errors.name
                  : undefined
              }
            />

            <Field
              as={Input}
              name="primaryValue"
              label="Cena zakupu"
              type="number"
              step="any"
              precision={2}
              maxLength={9}
              error={
                (formik.touched.primaryValue || formik.submitCount > 0) &&
                formik.errors.primaryValue
                  ? formik.errors.primaryValue
                  : undefined
              }
            />

            <Field
              as={SelectComponent}
              name="currencyUuid"
              label="Waluta"
              options={currencyOptions}
              isSearchable={false}
              error={
                (formik.touched.currencyUuid || formik.submitCount > 0) &&
                formik.errors.currencyUuid
                  ? formik.errors.currencyUuid
                  : undefined
              }
            />

            <Field
              as={Input}
              name="weight"
              label="Masa"
              type="number"
              precision={2}
              maxLength={11}
              suffix="g"
              optional
            />

            <Field
              as={Input}
              name="cut"
              label="Szlif"
              type="text"
              optional
              maxLength={100}
              error={
                (formik.touched.cut || formik.submitCount > 0) &&
                formik.errors.cut
                  ? formik.errors.cut
                  : undefined
              }
            />

            <Field
              as={Input}
              name="color"
              label="Barwa"
              type="text"
              optional
              maxLength={100}
              error={
                (formik.touched.color || formik.submitCount > 0) &&
                formik.errors.color
                  ? formik.errors.color
                  : undefined
              }
            />

            <Field
              as={Input}
              name="clarity"
              label="Czystość"
              type="text"
              optional
              maxLength={100}
              error={
                (formik.touched.clarity || formik.submitCount > 0) &&
                formik.errors.clarity
                  ? formik.errors.clarity
                  : undefined
              }
            />

            <Field
              component={Textarea}
              name="additionalInfo"
              label="Dodatkowe informacje"
              optional
            />

            <Field
              label="Umowa"
              name="contractFile"
              id="contractFile"
              as={DropFileInput}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              optional
              multiple={10}
              maxSize={5}
              description="Maksymalnie 10 plików, maks. 5 MB/plik (PDF, DOC, DOCX, JPG, JPEG, PNG)"
              existingFiles={existingContractFiles.map((f: any) => ({
                name: f.file?.name || "Plik",
                url: f.file?.url || "",
              }))}
              onServerFileRemoved={onRemovedContract}
            />

            <Field
              as={Checkbox}
              name="relianceCanView"
              label={RELIANCE_CONSENT_LABEL}
              checked={formik.values.relianceCanView}
              onChange={() =>
                formik.setFieldValue(
                  "relianceCanView",
                  !formik.values.relianceCanView,
                )
              }
            />
          </div>

          <div className="modal-buttons">
            <Button
              type="button"
              className="button--stroke button--rounded"
              onClick={onCancel}
              label="Anuluj"
            />
            <Button
              type="submit"
              className="button--black button--rounded"
              onClick={() => {}}
              label={isPending ? "Zapisywanie..." : "Zapisz"}
              isLoading={isPending}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default PreciousStonesForm;
